import type {
  CardFieldElementType,
  PaypercutElementsAppearance,
  ElementsErrorEvent,
  PaypercutPaymentMethodFutureUsage,
} from '../types/elements';
import { config } from '../config';
import { createBasisTheoryV2CardProvider } from './basis-theory-v2-card-provider';
import { BasisTheoryTokenizationIndeterminateError } from './basis-theory-tokenization';
import {
  CARD_COLLECTION_PROTOCOL_VERSION,
  LOCALIZED_CARD_COLLECTION_PROTOCOL_VERSION,
} from './card-collection-provider';
import type {
  CardCollectionBootstrap,
  CardCollectionConfirmationData,
  CardCollectionFieldChange,
  CardCollectionFieldEvents,
  CardCollectionFieldPresentation,
  CardCollectionProvider,
  CardCollectionProviderFactory,
  CardCollectionProviderField,
  CardFieldAppearanceCompiler,
  RenderableCardCollectionBootstrap,
} from './card-collection-provider';
import { resolveCardFieldCopy, type CardFieldCopy } from './card-field-copy';
import { cloneElementsAppearance } from './appearance';
import type { DeveloperAssistantCardValue } from './developer-assistant-registration';
import type { PreparedAppearanceTransition } from './appearance-transition-frame';

let internalProviderFactoryOverride: CardCollectionProviderFactory | null = null;

/** Repository-only fixture seam. It is not re-exported from the public package. */
export function setCardCollectionProviderFactoryForTesting(
  factory: CardCollectionProviderFactory | null,
): void {
  if (config.mode !== 'internal') {
    throw new Error('Card collection provider overrides are unavailable in production builds.');
  }
  internalProviderFactoryOverride = factory;
}

const resolveProviderFactory = (): CardCollectionProviderFactory =>
  internalProviderFactoryOverride ?? createBasisTheoryV2CardProvider;

type CardFieldRegistration = {
  readonly id: string;
  readonly type: CardFieldElementType;
  readonly target: HTMLElement;
  providerHost: HTMLElement;
  readonly ariaLabel: string;
  abortController: AbortController;
  readonly events: CardCollectionFieldEvents;
  presentation: CardCollectionFieldPresentation;
  destroyed: boolean;
  replacing: boolean;
  providerField: CardCollectionProviderField | null;
  providerGeneration: number;
  configurationRevision: number;
  updateRevision: number;
  updateChain: Promise<void>;
  pendingUpdate: CardCollectionProviderFieldUpdate | null;
  providerReady: boolean;
  lastChange: CardCollectionFieldChange | null;
};

type CardCollectionProviderFieldUpdate = Parameters<CardCollectionProviderField['update']>[0];

type StagedCardField = {
  readonly registration: CardFieldRegistration;
  readonly providerGeneration: number;
  readonly abortController: AbortController;
  readonly target: HTMLElement;
  providerField: CardCollectionProviderField | null;
  ready: boolean;
  activated: boolean;
  lastChange: CardCollectionFieldChange | null;
  reject(error: Error): void;
};

type PendingAppearancePreview = {
  readonly generation: number;
  readonly appearance: PaypercutElementsAppearance | undefined;
  readonly stages: StagedCardField[];
};

const REQUIRED_CARD_FIELD_TYPES: readonly CardFieldElementType[] = [
  'cardNumber',
  'cardExpiry',
  'cardCvc',
];
const SECURE_FIELD_SETTLE_MILLISECONDS = 150;
export const CARD_COLLECTION_APPEARANCE_PREVIEW_TIMEOUT_MS = 15_000;

export type MountCardCollectionFieldInput = {
  id: string;
  type: CardFieldElementType;
  target: HTMLElement;
  ariaLabel: string;
  placeholder: string;
  presentation: CardCollectionFieldPresentation;
  events: CardCollectionFieldEvents;
};

export class CardCollectionRuntimeError extends Error {
  constructor(
    readonly details: ElementsErrorEvent,
    cause?: unknown,
  ) {
    super(details.message);
    this.name = 'CardCollectionRuntimeError';
    if (cause !== undefined) (this as Error & { cause?: unknown }).cause = cause;
  }
}

const unavailableError = (): CardCollectionRuntimeError =>
  new CardCollectionRuntimeError({
    code: 'elements_capability_unsupported',
    message: 'Split card fields are not available for this payment configuration.',
    recoverable: false,
  });

const initializationError = (cause: unknown): CardCollectionRuntimeError =>
  cause instanceof CardCollectionRuntimeError
    ? cause
    : new CardCollectionRuntimeError(
        {
          code: 'elements_provider_initialization_failed',
          message: 'The secure card fields could not be initialized.',
          recoverable: true,
        },
        cause,
      );

const assertBootstrapCurrent = <T extends RenderableCardCollectionBootstrap>(bootstrap: T): T => {
  if (
    !Number.isFinite(Date.parse(bootstrap.expiresAt)) ||
    Date.parse(bootstrap.expiresAt) <= Date.now()
  ) {
    throw new CardCollectionRuntimeError({
      code: 'elements_session_expired',
      message: 'The secure card-field session has expired. Reload the payment form.',
      recoverable: false,
    });
  }
  return bootstrap;
};

const isAvailableBootstrap = (
  bootstrap: CardCollectionBootstrap | null,
): bootstrap is RenderableCardCollectionBootstrap =>
  (bootstrap?.protocolVersion === LOCALIZED_CARD_COLLECTION_PROTOCOL_VERSION ||
    bootstrap?.protocolVersion === CARD_COLLECTION_PROTOCOL_VERSION) &&
  'messages' in bootstrap;

const bootstrapUnavailableError = (
  bootstrap: CardCollectionBootstrap | null,
): CardCollectionRuntimeError => {
  if (
    (bootstrap?.protocolVersion === LOCALIZED_CARD_COLLECTION_PROTOCOL_VERSION ||
      bootstrap?.protocolVersion === CARD_COLLECTION_PROTOCOL_VERSION) &&
    'error' in bootstrap
  ) {
    return new CardCollectionRuntimeError(bootstrap.error);
  }
  return unavailableError();
};

export class CardCollectionRuntime {
  private readonly fields = new Map<string, CardFieldRegistration>();
  private bootstrap: RenderableCardCollectionBootstrap | undefined;
  private bootstrapFailure: CardCollectionRuntimeError | null = null;
  private bootstrapWaiters: Array<{
    resolve: (value: RenderableCardCollectionBootstrap) => void;
    reject: (error: CardCollectionRuntimeError) => void;
  }> = [];
  private provider: CardCollectionProvider | null = null;
  private providerPromise: Promise<CardCollectionProvider> | null = null;
  private readonly providerAbortController = new AbortController();
  private destroyed = false;
  private appearance: PaypercutElementsAppearance | undefined;
  private numberBrand: string | undefined;
  private appearancePreviewGeneration = 0;
  private providerGenerationSequence = 0;
  private providerTargetIdSequence = 0;
  private readonly providerTargetIds = new Set<string>();
  private pendingAppearancePreview: PendingAppearancePreview | null = null;

  constructor(
    initialAppearance: PaypercutElementsAppearance | undefined,
    private readonly compileAppearance: CardFieldAppearanceCompiler,
    private readonly providerFactory: CardCollectionProviderFactory = resolveProviderFactory(),
  ) {
    this.appearance = initialAppearance;
  }

  configure(bootstrap: CardCollectionBootstrap | null): void {
    if (this.destroyed || this.bootstrap !== undefined || this.bootstrapFailure) return;
    if (!isAvailableBootstrap(bootstrap)) {
      this.rejectBootstrap(bootstrapUnavailableError(bootstrap));
      return;
    }
    this.bootstrap = bootstrap;
    const waiters = this.bootstrapWaiters;
    this.bootstrapWaiters = [];
    waiters.forEach((waiter) => waiter.resolve(bootstrap));
  }

  async resolveFieldCopy(type: CardFieldElementType): Promise<CardFieldCopy> {
    if (this.destroyed) throw unavailableError();
    const bootstrap = assertBootstrapCurrent(await this.waitForBootstrap());
    return resolveCardFieldCopy(type, bootstrap.messages, bootstrap.locale);
  }

  fail(error: ElementsErrorEvent): void {
    if (this.destroyed || this.bootstrap !== undefined || this.bootstrapFailure) return;
    this.rejectBootstrap(new CardCollectionRuntimeError(error));
  }

  private rejectBootstrap(error: CardCollectionRuntimeError): void {
    this.bootstrapFailure = error;
    const waiters = this.bootstrapWaiters;
    this.bootstrapWaiters = [];
    waiters.forEach((waiter) => waiter.reject(error));
  }

  async mountField(input: MountCardCollectionFieldInput): Promise<void> {
    if (this.destroyed) throw unavailableError();
    if (this.fields.has(input.id)) {
      throw new Error('The secure card field is already mounted.');
    }

    this.assignProviderTargetId(
      input.target,
      `paypercut-${input.type}-provider-${input.id}`,
    );

    const registration: CardFieldRegistration = {
      id: input.id,
      type: input.type,
      target: input.target,
      providerHost: input.target,
      ariaLabel: input.ariaLabel,
      abortController: new AbortController(),
      events: input.events,
      presentation: { ...input.presentation },
      destroyed: false,
      replacing: false,
      providerField: null,
      providerGeneration: 0,
      configurationRevision: 0,
      updateRevision: 0,
      updateChain: Promise.resolve(),
      pendingUpdate: null,
      providerReady: false,
      lastChange: null,
    };
    this.fields.set(input.id, registration);

    try {
      const provider = await this.getProvider();
      if (registration.destroyed || this.fields.get(input.id) !== registration) return;

      const creationRevision = registration.configurationRevision;
      const providerGeneration = registration.providerGeneration;
      const providerField = await provider.createField({
        type: input.type,
        target: input.target,
        signal: registration.abortController.signal,
        style: this.compileAppearance(this.appearance, registration.presentation),
        ariaLabel: input.ariaLabel,
        placeholder: registration.presentation.placeholder,
        events: this.wrapEvents(registration, providerGeneration),
      });
      if (!this.isProviderGenerationCurrent(registration, providerGeneration)) {
        providerField.destroy();
        return;
      }
      registration.providerField = providerField;
      if (registration.configurationRevision !== creationRevision) {
        this.scheduleFieldUpdate(registration, {
          placeholder: registration.presentation.placeholder,
          style: this.compileAppearance(this.appearance, registration.presentation),
        });
        await registration.updateChain;
      }
      if (input.type === 'cardCvc' && this.numberBrand) {
        this.scheduleFieldUpdate(registration, { cardBrand: this.numberBrand });
        await registration.updateChain;
      }
    } catch (error) {
      if (this.destroyed) throw initializationError(error);
      if (!registration.destroyed && this.fields.get(input.id) === registration) {
        registration.providerField?.destroy();
        registration.providerField = null;
        this.fields.delete(input.id);
        throw initializationError(error);
      }
    }
  }

  command(id: string, action: 'clear' | 'focus' | 'blur'): void {
    const field = this.fields.get(id)?.providerField;
    if (!field) throw new Error('The secure card field is not ready.');
    field[action]();
  }

  updateFieldPresentation(id: string, presentation: CardCollectionFieldPresentation): void {
    if (this.destroyed) return;
    const registration = this.fields.get(id);
    if (!registration) return;
    registration.presentation = { ...presentation };
    registration.configurationRevision += 1;
    const field = registration.providerField;
    if (!field) return;
    this.scheduleFieldUpdate(registration, {
      placeholder: presentation.placeholder,
      style: this.compileAppearance(this.appearance, presentation),
    });
  }

  destroyField(id: string): void {
    const registration = this.fields.get(id);
    if (!registration) return;
    if (
      this.pendingAppearancePreview?.stages.some((stage) => stage.registration === registration)
    ) {
      this.cancelPendingAppearancePreview(
        new Error('A split card field was destroyed during Appearance Preview.'),
      );
    }
    registration.destroyed = true;
    registration.providerGeneration += 1;
    registration.replacing = false;
    registration.abortController.abort();
    registration.providerField?.destroy();
    registration.providerField = null;
    this.removeProviderHost(registration);
    this.fields.delete(id);
  }

  updateAppearance(appearance: PaypercutElementsAppearance | undefined): void {
    if (this.destroyed) return;
    this.appearance = appearance;
    for (const registration of this.fields.values()) {
      registration.configurationRevision += 1;
      const field = registration.providerField;
      if (!field) continue;
      this.scheduleFieldUpdate(registration, {
        style: this.compileAppearance(appearance, registration.presentation),
      });
    }
  }

  remountAppearance(appearance: PaypercutElementsAppearance | undefined): Promise<void> {
    return this.prepareAppearanceRemount(appearance).then((transition) => transition.commit());
  }

  prepareAppearanceRemount(
    appearance: PaypercutElementsAppearance | undefined,
  ): Promise<PreparedAppearanceTransition> {
    if (this.destroyed) return Promise.reject(unavailableError());
    this.cancelPendingAppearancePreview(
      new Error('A newer Appearance Preview superseded this replacement.'),
    );

    const pending: PendingAppearancePreview = {
      generation: ++this.appearancePreviewGeneration,
      appearance: cloneElementsAppearance(appearance),
      stages: [],
    };
    this.pendingAppearancePreview = pending;
    for (const registration of this.fields.values()) registration.replacing = true;

    return Promise.all(
      Array.from(this.fields.values()).map((registration) =>
        this.stageReplacementField(registration, pending),
      ),
    ).then(
      (stages) => {
        if (this.pendingAppearancePreview !== pending) {
          throw new Error('A newer Appearance Preview superseded this replacement.');
        }
        let settled = false;
        return {
          commit: () => {
            if (settled || this.pendingAppearancePreview !== pending) return;
            settled = true;
            this.appearance = pending.appearance;
            this.numberBrand = undefined;
            for (const stage of stages) this.activateStagedField(stage);
            this.pendingAppearancePreview = null;
          },
          rollback: () => {
            if (settled) return;
            settled = true;
            if (this.pendingAppearancePreview === pending) {
              this.pendingAppearancePreview = null;
            }
            this.discardStagedFields(pending);
            for (const registration of this.fields.values()) registration.replacing = false;
          },
        } satisfies PreparedAppearanceTransition;
      },
      (error: unknown) => {
        if (this.pendingAppearancePreview === pending) this.pendingAppearancePreview = null;
        this.discardStagedFields(pending);
        for (const registration of this.fields.values()) registration.replacing = false;
        throw error;
      },
    );
  }

  getAppearanceRemountGeneration(): number {
    return this.appearancePreviewGeneration;
  }

  cancelAppearanceRemount(generation: number, error: Error): void {
    if (this.pendingAppearancePreview?.generation !== generation) return;
    this.cancelPendingAppearancePreview(error);
  }

  supportsDeveloperPresets(): boolean {
    return this.bootstrap?.livemode === false;
  }

  announceAppearanceCommit(): void {
    if (this.destroyed) return;
    for (const registration of this.fields.values()) {
      registration.events.onChange(registration.lastChange ?? { complete: false, empty: true });
    }
  }

  canApplyDeveloperPreset(): boolean {
    return this.supportsDeveloperPresets() && this.resolveRequiredFields() !== null;
  }

  getSubmissionIssue(): { type: CardFieldElementType; errorType: 'incomplete' | 'invalid' } | null {
    const fields = this.resolveRequiredFields();
    if (!fields) {
      const missingType = REQUIRED_CARD_FIELD_TYPES.find(
        (type) =>
          !Array.from(this.fields.values()).some(
            (registration) =>
              registration.type === type &&
              registration.providerField !== null &&
              registration.providerReady,
          ),
      );
      return { type: missingType ?? 'cardNumber', errorType: 'incomplete' };
    }
    const invalid = fields.find((registration) => registration.lastChange?.errorType === 'invalid');
    if (invalid) return { type: invalid.type, errorType: 'invalid' };
    const incomplete = fields.find((registration) => registration.lastChange?.complete !== true);
    return incomplete ? { type: incomplete.type, errorType: 'incomplete' } : null;
  }

  assertCanSubmitPaymentMethod(): void {
    if (this.destroyed) throw unavailableError();
    if (this.bootstrapFailure) throw this.bootstrapFailure;
    if (!this.bootstrap) {
      throw new CardCollectionRuntimeError({
        code: 'elements_not_ready',
        message: 'The secure card fields are not ready yet.',
        recoverable: true,
      });
    }
    const bootstrap = assertBootstrapCurrent(this.bootstrap);
    if (
      bootstrap.protocolVersion !== CARD_COLLECTION_PROTOCOL_VERSION ||
      bootstrap.capabilities.paymentMethodSubmission !== true
    ) {
      throw new CardCollectionRuntimeError({
        code: 'elements_capability_unsupported',
        message: 'Split card-field submission is not supported by this Hosted Checkout version.',
        recoverable: true,
      });
    }
    if (
      this.pendingAppearancePreview ||
      Array.from(this.fields.values()).some((registration) => registration.replacing)
    ) {
      throw new CardCollectionRuntimeError({
        code: 'elements_not_ready',
        message: 'The secure card fields are refreshing their Appearance.',
        recoverable: true,
      });
    }
    const issue = this.getSubmissionIssue();
    if (issue) {
      throw new CardCollectionRuntimeError({
        code:
          issue.errorType === 'invalid'
            ? 'elements_payment_details_invalid'
            : 'elements_card_fields_incomplete',
        message:
          issue.errorType === 'invalid'
            ? 'Some card details are invalid.'
            : 'Please complete your card details.',
        recoverable: true,
      });
    }
  }

  async createConfirmationData(
    requestId: string,
    cardholderName?: string,
    futureUsage?: PaypercutPaymentMethodFutureUsage,
  ): Promise<CardCollectionConfirmationData> {
    this.assertCanSubmitPaymentMethod();
    try {
      const provider = await this.getProvider();
      return await provider.createConfirmationData(
        requestId,
        cardholderName?.trim() || undefined,
        futureUsage,
      );
    } catch (error) {
      if (error instanceof CardCollectionRuntimeError) throw error;
      if (error instanceof BasisTheoryTokenizationIndeterminateError) {
        throw new CardCollectionRuntimeError(
          {
            code: 'elements_submit_indeterminate',
            message:
              'Secure card tokenization may have completed, but its result could not be verified.',
            recoverable: false,
          },
          error,
        );
      }
      throw new CardCollectionRuntimeError(
        {
          code: 'elements_payment_method_creation_failed',
          message: 'Payment details are temporarily unavailable. Please try again.',
          recoverable: true,
        },
        error,
      );
    }
  }

  async applyDeveloperPreset(value: DeveloperAssistantCardValue): Promise<void> {
    if (!this.supportsDeveloperPresets()) {
      throw new Error('Split-card test presets are unavailable for this payment configuration.');
    }
    const fields = this.resolveRequiredFields();
    if (!fields) throw new Error('All three secure card fields must be ready before filling.');

    const values: Record<CardFieldElementType, CardCollectionProviderFieldUpdate['value']> = {
      cardNumber: value.number,
      cardExpiry: { month: value.expirationMonth, year: value.expirationYear },
      cardCvc: value.cvc,
    };
    const apply = async (): Promise<void> => {
      await Promise.all(
        fields.map((registration) =>
          this.enqueueDeveloperValueUpdate(registration, values[registration.type]),
        ),
      );
    };
    const settle = (): Promise<void> =>
      new Promise((resolve) => globalThis.setTimeout(resolve, SECURE_FIELD_SETTLE_MILLISECONDS));
    const allComplete = (): boolean =>
      fields.every((registration) => registration.lastChange?.complete === true);

    await apply();
    await settle();
    if (!allComplete()) {
      // Basis Theory 2.12.1 can acknowledge the first static-value update
      // before every nested field has completed asynchronous validation.
      await apply();
      await settle();
    }
    if (!allComplete()) throw new Error('The split-card test preset did not validate.');
  }

  private resolveRequiredFields(): CardFieldRegistration[] | null {
    const fields = REQUIRED_CARD_FIELD_TYPES.map((type) =>
      Array.from(this.fields.values()).find((registration) => registration.type === type),
    );
    return fields.every(
      (registration): registration is CardFieldRegistration =>
        registration?.providerField !== null && registration?.providerReady === true,
    )
      ? fields
      : null;
  }

  private enqueueDeveloperValueUpdate(
    registration: CardFieldRegistration,
    value: CardCollectionProviderFieldUpdate['value'],
  ): Promise<void> {
    const field = registration.providerField;
    if (!field || registration.destroyed || this.destroyed || value === undefined) {
      return Promise.reject(new Error('The secure card field is not ready.'));
    }
    const update = registration.updateChain
      .catch(() => undefined)
      .then(async () => {
        if (this.destroyed || registration.destroyed || registration.providerField !== field) {
          throw new Error('The secure card field is not ready.');
        }
        await field.update({ value });
      });
    registration.updateChain = update;
    return update;
  }

  private scheduleFieldUpdate(
    registration: CardFieldRegistration,
    update: CardCollectionProviderFieldUpdate,
  ): void {
    const field = registration.providerField;
    if (!field || registration.destroyed || this.destroyed) return;
    registration.pendingUpdate = { ...registration.pendingUpdate, ...update };
    const revision = ++registration.updateRevision;
    registration.updateChain = registration.updateChain
      .catch(() => undefined)
      .then(async () => {
        if (
          this.destroyed ||
          registration.destroyed ||
          registration.providerField !== field ||
          revision !== registration.updateRevision
        ) {
          return;
        }
        const pendingUpdate = registration.pendingUpdate;
        registration.pendingUpdate = null;
        if (pendingUpdate) await field.update(pendingUpdate);
      })
      .catch(() => {
        if (this.destroyed || registration.destroyed || registration.providerField !== field) {
          return;
        }
        registration.events.onError({
          code: 'elements_provider_update_failed',
          message: 'The secure card field could not be synchronized with the payment provider.',
          recoverable: true,
        });
      });
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.cancelPendingAppearancePreview(
      new Error('The secure card fields were destroyed during Appearance Preview.'),
    );
    this.providerAbortController.abort();
    for (const registration of this.fields.values()) {
      registration.destroyed = true;
      registration.abortController.abort();
      registration.providerField?.destroy();
      this.removeProviderHost(registration);
    }
    this.fields.clear();
    this.provider = null;
    this.providerPromise = null;
    const error = new CardCollectionRuntimeError({
      code: 'elements_destroyed',
      message: 'The secure card fields were destroyed before initialization completed.',
      recoverable: false,
    });
    this.bootstrapWaiters.forEach((waiter) => waiter.reject(error));
    this.bootstrapWaiters = [];
  }

  private wrapEvents(
    registration: CardFieldRegistration,
    providerGeneration: number,
  ): CardCollectionFieldEvents {
    const events = registration.events;
    return {
      ...events,
      onReady: () => {
        if (!this.isProviderGenerationCurrent(registration, providerGeneration)) return;
        registration.providerReady = true;
        registration.replacing = false;
        events.onReady();
      },
      onChange: (change) => {
        if (!this.isProviderGenerationCurrent(registration, providerGeneration)) return;
        registration.lastChange = change;
        if (registration.type === 'cardNumber') this.updateCardBrand(change.brand);
        events.onChange(change);
      },
      onFocus: () => {
        if (this.isProviderGenerationCurrent(registration, providerGeneration)) events.onFocus();
      },
      onBlur: () => {
        if (this.isProviderGenerationCurrent(registration, providerGeneration)) events.onBlur();
      },
      onError: (error) => {
        if (this.isProviderGenerationCurrent(registration, providerGeneration)) {
          events.onError(error);
        }
      },
    };
  }

  private stageReplacementField(
    registration: CardFieldRegistration,
    pending: PendingAppearancePreview,
  ): Promise<StagedCardField> {
    const abortController = new AbortController();
    const target = this.createStagingTarget(registration);
    let resolveStage!: (stage: StagedCardField) => void;
    let rejectStage!: (error: Error) => void;
    const promise = new Promise<StagedCardField>((resolve, reject) => {
      resolveStage = resolve;
      rejectStage = reject;
    });
    this.providerGenerationSequence = Math.max(
      this.providerGenerationSequence + 1,
      registration.providerGeneration + 1,
    );
    const stage: StagedCardField = {
      registration,
      providerGeneration: this.providerGenerationSequence,
      abortController,
      target,
      providerField: null,
      ready: false,
      activated: false,
      lastChange: null,
      reject: () => undefined,
    };
    let settled = false;
    const timeoutId = setTimeout(() => {
      stage.reject(new Error('A split card field did not become ready for Appearance Preview.'));
    }, CARD_COLLECTION_APPEARANCE_PREVIEW_TIMEOUT_MS);
    stage.reject = (error: Error): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      rejectStage(error);
    };
    pending.stages.push(stage);

    const resolveWhenReady = (): void => {
      if (!settled && stage.providerField && stage.ready) {
        settled = true;
        clearTimeout(timeoutId);
        resolveStage(stage);
      }
    };
    const events: CardCollectionFieldEvents = {
      onReady: () => {
        if (this.pendingAppearancePreview !== pending) return;
        stage.ready = true;
        if (stage.activated) registration.events.onReady();
        else resolveWhenReady();
      },
      onChange: (change) => {
        if (this.pendingAppearancePreview !== pending && !stage.activated) return;
        if (!stage.activated) {
          stage.lastChange = change;
          return;
        }
        if (!this.isProviderGenerationCurrent(registration, stage.providerGeneration)) return;
        registration.lastChange = change;
        if (registration.type === 'cardNumber') this.updateCardBrand(change.brand);
        registration.events.onChange(change);
      },
      onFocus: () => {
        if (
          stage.activated &&
          this.isProviderGenerationCurrent(registration, stage.providerGeneration)
        ) {
          registration.events.onFocus();
        }
      },
      onBlur: () => {
        if (
          stage.activated &&
          this.isProviderGenerationCurrent(registration, stage.providerGeneration)
        ) {
          registration.events.onBlur();
        }
      },
      onError: (error) => {
        if (stage.activated) {
          if (this.isProviderGenerationCurrent(registration, stage.providerGeneration)) {
            registration.events.onError(error);
          }
          return;
        }
        if (this.pendingAppearancePreview === pending) {
          stage.reject(new Error(error.message));
        }
      },
    };

    void this.getProvider().then(
      async (provider) => {
        if (this.pendingAppearancePreview !== pending) return;
        try {
          const providerField = await provider.createField({
            type: registration.type,
            target,
            signal: abortController.signal,
            style: this.compileAppearance(pending.appearance, registration.presentation),
            ariaLabel: registration.ariaLabel,
            placeholder: registration.presentation.placeholder,
            events,
          });
          if (this.pendingAppearancePreview !== pending) {
            providerField.destroy();
            return;
          }
          stage.providerField = providerField;
          resolveWhenReady();
        } catch (error) {
          stage.reject(
            error instanceof Error ? error : new Error('Appearance replacement failed.'),
          );
        }
      },
      (error: unknown) =>
        stage.reject(error instanceof Error ? error : new Error('Appearance replacement failed.')),
    );
    return promise;
  }

  private activateStagedField(stage: StagedCardField): void {
    const registration = stage.registration;
    const previousField = registration.providerField;
    const previousHost = registration.providerHost;
    registration.configurationRevision += 1;
    registration.updateRevision += 1;
    registration.pendingUpdate = null;
    registration.updateChain = Promise.resolve();
    registration.abortController.abort();
    registration.abortController = stage.abortController;
    registration.providerGeneration = stage.providerGeneration;
    registration.providerField = stage.providerField;
    registration.providerReady = true;
    registration.replacing = false;
    registration.lastChange = stage.lastChange ?? { complete: false, empty: true };
    stage.activated = true;
    try {
      previousField?.destroy();
    } catch {
      // The staged field is already ready and authoritative. A provider-owned
      // teardown failure must not split the coordinated visual commit.
    }
    if (previousHost !== registration.target) previousHost.remove();
    this.activateStagingTarget(stage.target, registration.target);
    registration.providerHost = stage.target;
  }

  private createStagingTarget(registration: CardFieldRegistration): HTMLElement {
    const target = registration.target;
    if (
      typeof document === 'undefined' ||
      typeof document.createElement !== 'function' ||
      typeof target.appendChild !== 'function'
    ) {
      return target;
    }
    const stagingTarget = document.createElement('div');
    // Basis Theory v2.12.1 suppresses fieldChange when targetId is empty.
    // A generation suffix also prevents the active and staged fields from
    // sharing an identity while an Appearance replacement is in flight.
    this.assignProviderTargetId(
      stagingTarget,
      `${target.id}-appearance-preview-${this.appearancePreviewGeneration}`,
    );
    stagingTarget.dataset.paypercutAppearanceStage = '';
    Object.assign(stagingTarget.style, {
      position: 'absolute',
      inset: '0',
      width: '100%',
      height: '100%',
      visibility: 'hidden',
      pointerEvents: 'none',
    });
    target.appendChild(stagingTarget);
    return stagingTarget;
  }

  private assignProviderTargetId(target: HTMLElement, preferredId: string): void {
    let candidate = target.id.trim() || preferredId;
    while (!candidate || this.providerTargetIds.has(candidate)) {
      candidate = `${preferredId}-${++this.providerTargetIdSequence}`;
    }
    target.id = candidate;
    this.providerTargetIds.add(candidate);
  }

  private activateStagingTarget(stagingTarget: HTMLElement, target: HTMLElement): void {
    if (stagingTarget === target) return;
    stagingTarget.removeAttribute?.('data-paypercut-appearance-stage');
    Object.assign(stagingTarget.style, {
      position: 'relative',
      inset: 'auto',
      width: '100%',
      height: '100%',
      visibility: 'visible',
      pointerEvents: 'auto',
    });
  }

  private removeProviderHost(registration: CardFieldRegistration): void {
    if (registration.providerHost !== registration.target) registration.providerHost.remove();
    registration.providerHost = registration.target;
  }

  private discardStagedFields(pending: PendingAppearancePreview): void {
    for (const stage of pending.stages) {
      stage.abortController.abort();
      stage.providerField?.destroy();
      if (stage.target !== stage.registration.target) stage.target.remove();
    }
  }

  private cancelPendingAppearancePreview(error: Error): void {
    const pending = this.pendingAppearancePreview;
    if (!pending) return;
    this.pendingAppearancePreview = null;
    this.discardStagedFields(pending);
    for (const stage of pending.stages) stage.reject(error);
    for (const registration of this.fields.values()) registration.replacing = false;
  }

  private isProviderGenerationCurrent(
    registration: CardFieldRegistration,
    providerGeneration: number,
  ): boolean {
    return (
      !this.destroyed &&
      !registration.destroyed &&
      this.fields.get(registration.id) === registration &&
      registration.providerGeneration === providerGeneration
    );
  }

  private updateCardBrand(brand: CardCollectionFieldChange['brand']): void {
    if (brand === this.numberBrand) return;
    this.numberBrand = brand;
    for (const registration of this.fields.values()) {
      if (registration.type !== 'cardCvc' || !registration.providerField || !brand) continue;
      this.scheduleFieldUpdate(registration, { cardBrand: brand });
    }
  }

  private async getProvider(): Promise<CardCollectionProvider> {
    if (this.destroyed) throw unavailableError();
    if (this.provider) return this.provider;
    if (this.providerPromise) return this.providerPromise;

    this.providerPromise = this.waitForBootstrap()
      .then(assertBootstrapCurrent)
      .then((bootstrap) =>
        this.providerFactory(bootstrap, { signal: this.providerAbortController.signal }),
      )
      .then((provider) => {
        if (this.destroyed) throw unavailableError();
        this.provider = provider;
        return provider;
      })
      .catch((error: unknown) => {
        this.providerPromise = null;
        throw initializationError(error);
      });
    return this.providerPromise;
  }

  private waitForBootstrap(): Promise<RenderableCardCollectionBootstrap> {
    if (this.bootstrapFailure) return Promise.reject(this.bootstrapFailure);
    if (this.bootstrap) return Promise.resolve(this.bootstrap);
    return new Promise<RenderableCardCollectionBootstrap>((resolve, reject) => {
      this.bootstrapWaiters.push({ resolve, reject });
    });
  }
}
